import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
	Navigate
} from "react-router-dom";

import AuthProvider from './auth/AuthProvider';
import RequireAuth from './auth/RequireAuth';

import './App.css';

import Setup from './containers/Setup';
import RedirectRoutes from './containers/RedirectRoutes';
import AuthRoot from 'containers/Auth/AuthRoot';
import Settings from 'containers/Settings/SettingsRoot';
import PageSection from 'components/PageSection';
import UserProfileSegment from 'components/UserProfileSegment';
import Profile from 'containers/Auth/Profile';
import OrgContextSelector from 'components/OrgContextSelector';
import OrgContextProvider, { OrgContextLink, RequireTenant, RequireTenantWorkspace, useOrgContext } from 'components/OrgContextProvider';
import DataCacheProvider from 'components/DataCacheProvider';
import TenantRoot from 'containers/TenantRoot/TenantRoot';
import WorkspaceRoot from 'containers/WorkspaceRoot/WorkspaceRoot';

interface SetupStatus {
	isConfigured: boolean;
}

const AppNav = () => {
	const ctx = useOrgContext();

	return (
		<p className="appNav">
			{ctx?.workspaceID && ctx?.contextPath && <li><OrgContextLink to="/">Workspace</OrgContextLink></li>}
			<li><OrgContextLink to="/settings">Settings</OrgContextLink></li>
			<li><UserProfileSegment /></li>
		</p>
	);
}

const AppHeader = () => {
	return (
		<React.Fragment>
			<PageSection borderEnd={true}>
				<div className="appHeader" style={{ display: 'flex', alignItems: 'center' }}>
					<OrgContextLink to={`/`}>
						<h1 style={{ margin: 0, marginRight: '1rem', position: 'relative', width: '48px', height: '48px' }}>
							<img src="/logo192.png" style={{ width: '48px', height: '48px', position: 'absolute', top: 0, left: 0 }} alt="Fastvue Platform Dev UI" title="Fastvue Platform Dev UI" />
							<span style={{ position: 'absolute', bottom: 0, left: 0, width: '48px', height: '48px', display: 'flex', alignItems: 'end', justifyContent: 'center', color: '#ffffff', fontSize: '10pt', fontWeight: 'bold' }}>Dev UI</span>
						</h1>
					</OrgContextLink>
					<span style={{ marginRight: 'auto' }}><OrgContextSelector /></span>
					<AppNav />
				</div>
			</PageSection>
		</React.Fragment>
	);
}

const AppOrgContextRoot = () => {
	const ctx = useOrgContext();

	return (
		<Routes>
			<Route path="/settings/*" element={<Settings />} />
			<Route path="/profile/*" element={<Profile />} />
			{ctx?.tenantID && ctx?.workspaceID && <Route path="/*" element={<WorkspaceRoot />} />}
			{ctx?.tenantID && !ctx?.workspaceID && <Route path="/*" element={<TenantRoot />} />}
		</Routes>
	);
}

const AppRoutes = () => {
	const ctx = useOrgContext();

	return (
		<Routes key={`${ctx?.tenantID ?? '__notenant__'}-${ctx?.workspaceID ?? '__noworkspace__'}`}>
			<Route path="/" element={<div>Select a Tenant/Workspace above!</div>} />
			<Route path="/t/:tenantID/w/:workspaceID/*" element={<RequireAuth><RequireTenantWorkspace><AppOrgContextRoot /></RequireTenantWorkspace></RequireAuth>} />
			<Route path="/t/:tenantID/*" element={<RequireAuth><RequireTenant><AppOrgContextRoot /></RequireTenant></RequireAuth>} />
			<Route path="/w/:workspaceID/*" element={<RequireAuth><RequireTenantWorkspace><AppOrgContextRoot /></RequireTenantWorkspace></RequireAuth>} />
			<Route path="/*" element={<RequireAuth><AppOrgContextRoot /></RequireAuth>} />
		</Routes>
	);
}

const AppRoot = () => {
	return (
		<div>
			<OrgContextProvider>
				<DataCacheProvider>
					<AppHeader />

					<AppRoutes />
				</DataCacheProvider>
			</OrgContextProvider>
		</div>
	);
};

const App = () => {
	const [setupStatus, setSetupStatus] = React.useState<SetupStatus | null>(null);

	const loadSetupStatus = () => {
		fetch('/api/setup/status')
		.then(res => res.json())
		.then((json: { data?: SetupStatus }) => {
			if (json.data) setSetupStatus(json.data);
		});
	};

	React.useEffect(() => {
		loadSetupStatus();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="App">
			<Router>
				<AuthProvider>
					{
						setupStatus && (
							setupStatus.isConfigured ? (
								<Routes>
									<Route path="/auth/*" element={<AuthRoot />} />
									<Route path="/redirect/*" element={<RedirectRoutes />} />
									<Route path="/*" element={<RequireAuth><AppRoot /></RequireAuth>} />
								</Routes>
							) : (
								<Routes>
									<Route path="/setup" element={<Setup onComplete={loadSetupStatus} />} />
									<Route path="/*" element={<Navigate to="/setup" />} />
								</Routes>
							)
						)
					}
				</AuthProvider>
			</Router>
		</div>
	);
};

export default App;
